import { useMemo } from "react";
import { Route } from "@/routes/(auth)/(headless)/reports/clients/$id.portfolio-review.$reviewId";
import {
  ExternalHoldingsSummary,
  ExternalSipsSummary,
} from "@/modules/portfolio-review/components/external-holdings-section";
import { Report } from "../components";
import { ReportContainer } from "../components/report-container";
import { ReportFooter } from "../components/report-footer";
import { ReportHeader } from "../components/report-header";
import { CoverSection } from "../components/proposal/cover-section";
import { ReportSection } from "../components/proposal/report-section";
import {
  AmcFundExposurePage,
  HoldingsExposurePage,
} from "../components/portfolio-review/exposure-pages";
import {
  AssetAllocationPage,
  PerformancePage,
  PortfolioOverviewPage,
} from "../components/portfolio-review/overview-pages";
import {
  FundsToExitPage,
  FundsToHoldPage,
  FundsToSwitchPage,
  RecommendationsSummary,
} from "../components/portfolio-review/recommendation-pages";
import { ReportPageTitle } from "../components/portfolio-review/report-blocks";
import {
  externalHoldingsOf,
  leafSectionIds,
  reviewReportSections,
  useAmcNames,
} from "../components/portfolio-review/report-data";

// Subsections that share the one "Recommendations" title.
const RECOMMENDATION_SECTIONS = [
  "recommendations-summary",
  "funds-to-hold",
  "funds-to-switch",
  "funds-to-exit",
];

export function PortfolioReviewReport() {
  const {
    client,
    employee,
    distributorProfile,
    distributorBranding,
    review,
    report,
    overview,
  } = Route.useLoaderData();
  const { embed, sections } = Route.useSearch();
  const amcName = useAmcNames(review);
  const external = externalHoldingsOf(overview);

  const available = useMemo(
    () =>
      new Set(
        leafSectionIds(reviewReportSections({ review, report, overview }))
      ),
    [review, report, overview]
  );
  const show = (id: string) =>
    available.has(id) && (!sections || sections.includes(id));

  return (
    <Report embed={!!embed}>
      <style>{`
        @page {
          size: B4 landscape !important;
          margin: 0;
        }
      `}</style>
      <ReportHeader
        distributorBranding={distributorBranding}
        profile={distributorProfile}
      />
      <ReportContainer>
        <div className="content w-full space-y-10 px-8">
          <ReportSection sectionId="cover" visible={show("cover")}>
            <CoverSection
              client={client}
              title="Portfolio Review"
              createdAt={review.created_at ?? review.as_of_date ?? ""}
              distributorProfile={distributorProfile}
              employee={employee}
            />
          </ReportSection>

          <ReportSection
            sectionId="performance"
            className="break-before-page"
            visible={show("performance")}
          >
            <PerformancePage report={report} />
          </ReportSection>

          <ReportSection
            sectionId="portfolio-overview"
            className="break-before-page"
            visible={show("portfolio-overview")}
          >
            <PortfolioOverviewPage
              review={review}
              report={report}
              amcName={amcName}
            />
          </ReportSection>

          {external ? (
            <>
              <ReportSection
                sectionId="external-holdings"
                className="break-before-page"
                visible={show("external-holdings")}
              >
                <div className="flex flex-col gap-6">
                  <ReportPageTitle title="External holdings" />
                  <ExternalHoldingsSummary holdings={external} />
                </div>
              </ReportSection>

              <ReportSection
                sectionId="external-sips"
                className="break-before-page"
                visible={show("external-sips")}
              >
                <div className="flex flex-col gap-6">
                  <ReportPageTitle title="Ongoing external SIPs" />
                  <ExternalSipsSummary sips={external.ongoing_sips} />
                </div>
              </ReportSection>
            </>
          ) : null}

          <ReportSection
            sectionId="asset-allocation"
            className="break-before-page"
            visible={show("asset-allocation")}
          >
            <AssetAllocationPage report={report} />
          </ReportSection>

          {RECOMMENDATION_SECTIONS.some(show) ? (
            <div className="flex break-before-page flex-col gap-8">
              <ReportSection
                sectionId="recommendations-summary"
                visible={show("recommendations-summary")}
                className="gap-8 flex flex-col"
              >
                <ReportPageTitle title="Recommendations" />
                <RecommendationsSummary review={review} />
                <FundsToHoldPage review={review} />
                <FundsToSwitchPage review={review} />
                <FundsToExitPage review={review} />
              </ReportSection>
            </div>
          ) : null}

          <ReportSection
            sectionId="amc-fund-exposure"
            className="break-before-page"
            visible={show("amc-fund-exposure")}
          >
            <AmcFundExposurePage
              review={review}
              report={report}
              amcName={amcName}
            />
          </ReportSection>

          <ReportSection
            sectionId="stock-exposure"
            className="break-before-page"
            visible={show("stock-exposure")}
          >
            <HoldingsExposurePage
              title="Stock Exposure"
              holdings={report.exposure.top_stocks}
            />
          </ReportSection>

          <ReportSection
            sectionId="sector-exposure"
            className="break-before-page"
            visible={show("sector-exposure")}
          >
            <HoldingsExposurePage
              title="Sector Exposure"
              holdings={report.exposure.top_sectors}
            />
          </ReportSection>
        </div>
      </ReportContainer>
      <ReportFooter>{`${client.name}’s Portfolio Review`}</ReportFooter>
    </Report>
  );
}
